<template>
  <header class="ni-hero">
    <div class="ni-hero__stage">
      <swiper
        v-if="slides.length"
        class="ni-hero__swiper"
        :modules="modules"
        :slides-per-view="1"
        :loop="slides.length > 1"
        :pagination="{ clickable: true }"
        :autoplay="slides.length > 1 ? { delay: 4000, disableOnInteraction: false } : false"
      >
        <swiper-slide v-for="(banner, index) in slides" :key="banner.id || index">
          <img
            class="ni-hero__slide"
            :src="banner.thumb"
            alt=""
            @click="openBanner(banner)"
          />
        </swiper-slide>
      </swiper>
      <div v-else class="ni-hero__fallback"></div>

      <div class="ni-hero__bar">
        <a href="#home" class="ni-hero__logo" @click="handleNavClick('#home', $event)">
          <img v-if="logoUrl" :src="logoUrl" alt="HI QINGDAO" />
        </a>

        <nav class="ni-hero__nav" aria-label="主导航">
          <div
            v-for="item in navItems"
            :key="item.id"
            class="ni-hero__item"
          >
            <a
              :href="item.href"
              class="ni-hero__link"
              :class="{ 'is-active': activeNav === item.anchor }"
              @click="handleMenuClick(item, $event)"
            >
              <span>{{ item.label }}</span>
              <i v-if="item.children.length" class="iconfont icon-2701jiantou3 ni-hero__arrow" aria-hidden="true"></i>
            </a>
            <div v-if="item.children.length" class="ni-hero__submenu">
              <a
                v-for="child in item.children"
                :key="child.id"
                :href="child.href"
                class="ni-hero__sublink"
                :target="child.target === '_blank' ? '_blank' : undefined"
                :rel="child.target === '_blank' ? 'noopener noreferrer' : undefined"
                @click="handleMenuClick(child, $event)"
              >
                {{ child.label }}
              </a>
            </div>
          </div>
        </nav>

        <div class="ni-hero__side">
          <div class="ni-hero__lang">
            <NewIndexChangeLang :show-icon="false" tone="light" />
          </div>
          <div v-if="!isLoggedIn" class="ni-hero__login-box" @click="handleLogin">
            <button type="button" class="ni-hero__login-icon" :title="t('loginOrRegister')">
              <i class="iconfont icon-icontouxiang" aria-hidden="true"></i>
            </button>
            <span class="ni-hero__login-text">{{ t('loginOrRegister') }}</span>
          </div>
          <div v-else class="ni-hero__login-box ni-hero__user">
            <button type="button" class="ni-hero__login-icon" :title="username" @click="toUserHome">
              <i class="iconfont icon-icontouxiang" aria-hidden="true"></i>{{ username }}
            </button>
            <span class="ni-hero__login-text" role="button" tabindex="0">
              <i class="iconfont icon-icontouxiang" aria-hidden="true"></i>{{ username }}
            </span>
            <div class="ni-hero__user-menu">
              <i class="ni-hero__user-arrow" aria-hidden="true"></i>
              <button
                v-for="item in userMenuItems"
                :key="item.id"
                type="button"
                @click.stop="handleUserMenu(item)"
              >
                {{ item.name }}
              </button>
            </div>
          </div>
          <button type="button" class="ni-hero__burger" aria-label="menu" @click="mobileMenuOpen = !mobileMenuOpen">
            <i class="iconfont icon-listo" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <form class="ni-hero__search" @submit.prevent="handleSearchSubmit">
        <input
          v-model="searchQuery"
          type="text"
          class="ni-hero__search-input"
          :placeholder="t('search.trySearch')"
          @keyup.enter="handleSearchSubmit"
        />
        <button type="button" class="ni-hero__cam" aria-label="image search" @click="uploadVisible = true">
          <i class="iconfont icon-zhaoxiangji" aria-hidden="true"></i>
        </button>
        <button type="submit" class="ni-hero__search-btn">
          <i class="iconfont icon-sousuo1" aria-hidden="true"></i>
          <span>{{ t('search.search') }}</span>
        </button>
      </form>
    </div>

    <div v-if="mobileMenuOpen" class="ni-hero__drawer">
      <div v-for="item in navItems" :key="item.id" class="ni-hero__drawer-item">
        <a :href="item.href" @click="closeMobileMenu(item, $event)">{{ item.label }}</a>
        <div v-if="item.children.length" class="ni-hero__drawer-sub">
          <a
            v-for="child in item.children"
            :key="child.id"
            :href="child.href"
            :target="child.target === '_blank' ? '_blank' : undefined"
            @click="closeMobileMenu(child, $event)"
          >
            {{ child.label }}
          </a>
        </div>
      </div>
    </div>

    <div v-show="uploadVisible" class="ni-hero__upload">
      <div class="ni-hero__upload-dialog">
        <button type="button" class="ni-hero__upload-close" @click="uploadVisible = false">×</button>
        <p class="ni-hero__upload-title">{{ t('search.takePic') }}</p>
        <label class="ni-hero__upload-drop">
          <input type="file" accept="image/*" @change="onImageFile" />
          <span>{{ t('search.clickUpload') }}</span>
        </label>
      </div>
    </div>
  </header>
</template>

<script setup>
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import NewIndexChangeLang from './NewIndexChangeLang.vue';
import { SHOW_ASK_QINGDAO_SECTION } from '@/config/newIndexDataSource.js';
import { isExpandOnly, navHref, navLabel } from '@/utils/newIndexNav.js';

const props = defineProps({
  isLoggedIn: { type: Boolean, default: false },
  username: { type: String, default: '' },
  navTree: { type: Array, default: () => [] },
  sliders: { type: Array, default: () => [] },
  logoUrl: { type: String, default: '' },
});

const emit = defineEmits(['search', 'login']);
const router = useRouter();
const store = useStore();
const { proxy } = getCurrentInstance();
const { t, locale } = useI18n();
const modules = [Autoplay, Pagination];

const searchQuery = ref('');
const uploadVisible = ref(false);
const mobileMenuOpen = ref(false);
const activeNav = ref('#home');

let scrollContainer = null;
let scrollRaf = null;

const slides = computed(() => (props.sliders || []).filter((item) => item && item.thumb));

const userMenuItems = computed(() => [
  { id: 1, name: t('personCenter'), type: 'user' },
  { id: 3, name: t('sideMenu.notice'), type: 'notice' },
  { id: 2, name: t('logout'), type: 'exit' },
]);

const mapNavItem = (item) => ({
  ...item,
  label: navLabel(item, locale.value),
  href: navHref(item),
  anchor: item.type === 'anchor' ? item.value : '',
  target: item.target || '_self',
  children: (item.children || []).map((child) => ({
    ...child,
    label: navLabel(child, locale.value),
    href: navHref(child),
    target: child.target || '_self',
  })),
});

const navItems = computed(() => (props.navTree || []).map(mapNavItem));
const baseSectionIds = ['#home', '#latest-news', '#qingdao-world', '#int-communication', '#activities', '#photo-depot', '#qd-service'];
const sectionIds = SHOW_ASK_QINGDAO_SECTION
  ? baseSectionIds
  : baseSectionIds.filter((id) => id !== '#qd-service');
const navHrefs = computed(() => navItems.value.map((item) => item.anchor).filter(Boolean));

function openBanner(banner) {
  const url = banner?.url;
  if (!url) return;
  if (/^https?:\/\//i.test(url)) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }
  router.push(url);
}

function handleLogin() {
  mobileMenuOpen.value = false;
  emit('login');
}

function toUserHome() {
  router.push('/my/home');
}

function handleUserMenu(item) {
  if (item.type === 'exit') {
    proxy.$cookies.removeToken();
    proxy.$cookies.removeUsername();
    proxy.$cookies.removeMobile();
    proxy.$cookies.removeTokentype();
    proxy.$cookies.removeAuthKey();
    location.reload();
    return;
  }
  if (item.type === 'user') router.push('/my/setting');
  if (item.type === 'notice') router.push('/my/notice');
}

function handleSearchSubmit() {
  const query = searchQuery.value.trim();
  if (!query) return;
  emit('search', { query, category: 'photos' });
}

function onImageFile(event) {
  const file = event.target.files && event.target.files[0];
  if (!file) return;
  uploadVisible.value = false;
  store.dispatch('common/postPic', { file }).then((res) => {
    if (res) router.push({ path: '/photos', query: { file: res } });
  }).catch(() => {});
  event.target.value = '';
}

function getScrollContainer() {
  return document.querySelector('.ni-page') || document.scrollingElement || document.documentElement;
}

function getHeaderOffset() {
  const bar = document.querySelector('.ni-hero__bar');
  return bar ? bar.offsetHeight : 0;
}

function getSectionTop(container, el) {
  const containerRect = container.getBoundingClientRect();
  return el.getBoundingClientRect().top - containerRect.top + container.scrollTop;
}

function resolveNavHref(sectionHref) {
  const hrefs = navHrefs.value;
  if (hrefs.includes(sectionHref)) return sectionHref;
  const idx = sectionIds.indexOf(sectionHref);
  for (let i = idx; i >= 0; i -= 1) {
    if (hrefs.includes(sectionIds[i])) return sectionIds[i];
  }
  return '#home';
}

function updateActiveNavFromScroll() {
  if (!scrollContainer) return;
  const scrollTop = scrollContainer.scrollTop;
  if (scrollTop < 8) {
    activeNav.value = '#home';
    return;
  }
  const threshold = scrollTop + getHeaderOffset() + 48;
  let currentSection = '#home';
  sectionIds.forEach((href) => {
    const el = document.getElementById(href.replace('#', ''));
    if (!el) return;
    if (getSectionTop(scrollContainer, el) <= threshold) currentSection = href;
  });
  activeNav.value = resolveNavHref(currentSection);
}

function handleScroll() {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = null;
    updateActiveNavFromScroll();
  });
}

function scrollToAnchor(href) {
  const container = getScrollContainer();
  if (href === '#home') {
    container.scrollTo({ top: 0, behavior: 'smooth' });
    return;
  }
  const el = document.getElementById(href.replace('#', ''));
  if (!el) return;
  const containerRect = container.getBoundingClientRect();
  const elTop = el.getBoundingClientRect().top - containerRect.top + container.scrollTop;
  container.scrollTo({ top: Math.max(0, elTop - getHeaderOffset()), behavior: 'smooth' });
}

function handleNavClick(href, event) {
  event.preventDefault();
  activeNav.value = href;
  scrollToAnchor(href);
}

function handleMenuClick(item, event) {
  if (isExpandOnly(item)) {
    event?.preventDefault?.();
    return;
  }
  const href = item?.href || '';
  if (item?.target === '_blank' && href && href !== '#') {
    event?.preventDefault?.();
    window.open(href, '_blank', 'noopener,noreferrer');
    return;
  }
  if (href.startsWith('#')) {
    handleNavClick(href, event || { preventDefault() {} });
    return;
  }
  event?.preventDefault?.();
  if (!href || href === '#') return;
  if (/^https?:\/\//i.test(href)) {
    window.location.href = href;
    return;
  }
  router.push(href);
}

function closeMobileMenu(item, event) {
  mobileMenuOpen.value = false;
  handleMenuClick(item, event || { preventDefault() {} });
}

onMounted(() => {
  scrollContainer = getScrollContainer();
  updateActiveNavFromScroll();
  scrollContainer?.addEventListener('scroll', handleScroll, { passive: true });
});

onBeforeUnmount(() => {
  scrollContainer?.removeEventListener('scroll', handleScroll);
  if (scrollRaf) cancelAnimationFrame(scrollRaf);
});
</script>
